// ================================================================
// 🎟️ COUPONS REPORT - Relatório de Cupons
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const CouponsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [couponsData, setCouponsData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsColors = useChartsColors();

  const loadCouponsReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🎟️ Carregando Relatório de Cupons com filtros...', filtersToUse);

      const result = await ReportsService.getCouponsReport({
        period: filtersToUse.period,
        product_id: filtersToUse.advancedFilters?.product_id
      });

      if (result.success) {
        setCouponsData(result.data);
        console.log('✅ Relatório de Cupons carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCouponsReport = useCallback(async () => {
    loadCouponsReportWithFilters(filters);
  }, [filters, loadCouponsReportWithFilters]);

  useEffect(() => {
    loadCouponsReport();
  }, [loadCouponsReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadCouponsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadCouponsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const couponsUsageData = couponsData?.charts?.couponsUsage ? {
    labels: couponsData.charts.couponsUsage.map(item => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [{
      label: 'Usos',
      data: couponsData.charts.couponsUsage.map(item => item.uses_count),
      borderColor: '#007bff',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      tension: 0.4
    }, {
      label: 'Cupons Únicos',
      data: couponsData.charts.couponsUsage.map(item => item.unique_coupons),
      borderColor: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  const topCouponsData = couponsData?.charts?.topCoupons ? {
    labels: couponsData.charts.topCoupons.map(item => item.code?.substring(0, 15) + '...'),
    datasets: [{
      label: 'Usos',
      data: couponsData.charts.topCoupons.map(item => item.uses_count),
      backgroundColor: '#007bff',
      borderWidth: 0
    }]
  } : { labels: [], datasets: [] };

  const couponsColumns = [
    {
      key: 'code',
      label: 'Código',
      sortable: true,
      render: (value, row) => (
        <div>
          <strong>{value}</strong>
          <br />
          <small style={{ color: '#6c757d' }}>{row.name}</small>
        </div>
      )
    },
    {
      key: 'discount_display',
      label: 'Desconto',
      sortable: true
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: value === 'active' ? '#d4edda' : '#f8d7da',
          color: value === 'active' ? '#155724' : '#721c24'
        }}>
          {value === 'active' ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      key: 'product_name',
      label: 'Produto',
      sortable: true
    },
    {
      key: 'actual_uses',
      label: 'Usos',
      sortable: true,
      render: (value, row) => (
        <span>
          {value} {row.max_uses ? `/ ${row.max_uses}` : ''}
        </span>
      )
    },
    {
      key: 'valid_from',
      label: 'Válido de',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A'
    },
    {
      key: 'valid_until',
      label: 'Válido até',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A'
    }
  ];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:ticket-percent" style={{ fontSize: '22px', color: '#ffc107' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Relatório de Cupons
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowFilterDrawer(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '20px' }} />
                  Filtros Avançados
                  {Object.keys(filters.advancedFilters).length > 0 && (
                    <span style={{
                      backgroundColor: '#007bff',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {Object.keys(filters.advancedFilters).length}
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadCouponsReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadCouponsReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados dos cupons...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && couponsData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={couponsData.kpis.total_coupons.label}
                  value={couponsData.kpis.total_coupons.value}
                  icon="mdi:ticket-percent"
                  color="#007bff"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={couponsData.kpis.active_coupons.label}
                  value={couponsData.kpis.active_coupons.value}
                  icon="mdi:check-circle"
                  color="#28a745"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={couponsData.kpis.total_uses.label}
                  value={couponsData.kpis.total_uses.value}
                  icon="mdi:chart-line"
                  color="#17a2b8"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={couponsData.kpis.total_discount_value.label}
                  value={couponsData.kpis.total_discount_value.formatted}
                  icon="mdi:currency-usd"
                  color="#ffc107"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={8} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='textcontrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Uso de Cupons
                    </span>
                    <Line data={couponsUsageData} options={{ responsive: true, maintainAspectRatio: true, scales: { x: { grid: { color: chartsColors } }, y: { grid: { color: chartsColors }}} }} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='textcontrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Top 10 Cupons
                    </span>
                    <Bar data={topCouponsData} options={{ responsive: true, maintainAspectRatio: true, scales: { x: { grid: { color: chartsColors } }, y: { grid: { color: chartsColors }}} }} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Coupons Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='textcontrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Lista de Cupons
                </span>
                <SmartTable
                  columns={couponsColumns}
                  data={couponsData.couponsList || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default CouponsReport;

